import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronsUp, Power, PowerOff, Ban, ShieldAlert, Radio, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STAGE_STYLE = (s) =>
  s >= 4 ? "bg-status-recovery/15 text-status-recovery"
  : s >= 2 ? "bg-status-overdue/15 text-status-overdue"
  : "bg-status-available/15 text-status-available";

export default function RecoveryBoard() {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [blocklist, setBlocklist] = useState([]);
  const timer = useRef(null);

  const load = useCallback(async () => {
    const [b, bl] = await Promise.all([api.get("/operator/recovery"), api.get("/operator/blocklist")]);
    setBoard(b.data); setBlocklist(bl.data);
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const escalate = (id) => act(() => api.post(`/operator/bookings/${id}/escalate`), "Escalated to next rung");
  const immobilize = (id) => act(() => api.post(`/operator/bookings/${id}/immobilize`), "Remote disable requested");
  const reenable = (id) => act(() => api.post(`/operator/bookings/${id}/enable`), "Vehicle power restored");
  const blocklistRider = (cid) => act(() => api.post("/operator/blocklist", { customer_id: cid, reason: "Recovery / abuse" }), "Rider added to blocklist");

  if (!board) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Recovery</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Overdue rentals escalate through the ladder. Remote disable is stationary-only and capability-gated per provider.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {board.ladder.map((r) => (
          <span key={r.stage} className={`rounded-full px-2.5 py-1 text-xs font-medium ${STAGE_STYLE(r.stage)}`}>
            {r.stage}. {r.label}
          </span>
        ))}
      </div>

      {board.rentals.length === 0 ? (
        <div className="rounded-tenant-card border border-dashed border-tenant-border p-12 text-center">
          <ShieldAlert className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No active rentals to monitor.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {board.rentals.map((r) => (
            <div key={r.booking_id} data-testid={`recovery-row-${r.booking_id}`}
              className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 shadow-tenant-soft">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{r.customer_name} · {r.vehicle_reg}</p>
                  <p className="text-sm text-muted-foreground">{r.vehicle_model} · {r.provider}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {r.overdue_hours > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-status-overdue/15 px-2.5 py-1 text-xs font-medium text-status-overdue">
                      <AlertTriangle className="h-3 w-3" /> {r.overdue_hours}h overdue
                    </span>
                  )}
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STAGE_STYLE(r.escalation_stage)}`} data-testid={`recovery-stage-${r.booking_id}`}>
                    Stage {r.escalation_stage} · {r.stage_label}
                  </span>
                  {r.recommended_stage > r.escalation_stage && (
                    <span className="rounded-full border border-dashed border-status-recovery/40 px-2.5 py-1 text-xs text-status-recovery">
                      recommended {r.recommended_stage}
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Radio className="h-3 w-3" /> {r.stationary ? "Parked" : "Moving"}</span>
                <span>Immobilise: {r.can_immobilise ? "supported" : "software-notice only"}</span>
                {r.immobilized && <span className="font-medium text-status-recovery">● Immobilised</span>}
                {r.disable_requested && !r.immobilized && <span className="font-medium text-status-overdue">● Disable armed (awaiting parked)</span>}
                {r.geofence_status === "breach" && <span className="font-medium text-status-recovery">● Geofence breach</span>}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button data-testid={`escalate-${r.booking_id}`} size="sm" variant="outline" className="gap-1.5"
                  disabled={r.escalation_stage >= 5} onClick={() => escalate(r.booking_id)}>
                  <ChevronsUp className="h-3.5 w-3.5" /> Escalate
                </Button>
                {user?.role === "owner" && !r.immobilized && r.escalation_stage < 5 && (
                  <Button data-testid={`immobilize-${r.booking_id}`} size="sm" variant="outline" className="gap-1.5 text-status-recovery"
                    onClick={() => immobilize(r.booking_id)}>
                    <PowerOff className="h-3.5 w-3.5" /> Remote disable
                  </Button>
                )}
                {r.escalation_stage >= 5 && (
                  <span data-testid={`recovered-${r.booking_id}`} className="inline-flex items-center gap-1.5 rounded-tenant-button bg-status-recovery/15 px-3 py-1.5 text-xs font-medium text-status-recovery">
                    <ShieldAlert className="h-3.5 w-3.5" /> In recovery
                  </span>
                )}
                {user?.role === "owner" && r.immobilized && (
                  <Button data-testid={`enable-${r.booking_id}`} size="sm" variant="outline" className="gap-1.5 text-status-available"
                    onClick={() => reenable(r.booking_id)}>
                    <Power className="h-3.5 w-3.5" /> Re-enable
                  </Button>
                )}
                {user?.role === "owner" && (
                  <Button data-testid={`blocklist-${r.booking_id}`} size="sm" variant="outline" className="gap-1.5"
                    onClick={() => blocklistRider(r.customer_id)}>
                    <Ban className="h-3.5 w-3.5" /> Blocklist rider
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <section>
        <h2 className="mb-3 text-lg font-semibold">Blocklist <span className="ml-1 text-sm font-normal text-muted-foreground">{blocklist.length}</span></h2>
        {blocklist.length === 0 ? (
          <p className="text-sm text-muted-foreground">No blocked riders.</p>
        ) : (
          <div className="space-y-2">
            {blocklist.map((b) => (
              <div key={b.id} data-testid={`blocklist-item-${b.id}`} className="flex items-center justify-between rounded-tenant-card border border-tenant-border bg-tenant-surface px-4 py-3 text-sm">
                <span className="flex items-center gap-2"><Ban className="h-4 w-4 text-status-recovery" /> {b.name}</span>
                <span className="text-muted-foreground">{b.reason}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
